import React, { FC, useCallback, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import classnames from 'classnames';
import Media from 'react-media';
import { Route, useRouteMatch } from 'react-router-dom';
import { startOfMonth } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Button from 'src/common/components/Button';
import getClassName from 'src/common/utils/getClassName';
import Notification from 'src/common/components/Notification';
import apiFetch from 'src/pubsub/apiFetch';
import isCalendarSyncAllowed from 'src/calendar/components/SyncCalendarPage/utils/isCalendarSyncAllowed';
import { getUser } from 'src/common/utils/reduxStoreSelectors';
import { MEDIA_QUERIES, PATHS } from '../../../common/constants';
import useBoats from '../../../common/hooks/useBoats';
import LoadingSpinner from '../../../common/components/LoadingSpinner';
import ScrollToMe from '../../../common/components/ScrollToMe';
import Svg from '../../../common/components/Svg';
import { useCalendarContext, MAX_NEXT_MONTHS, MAX_NEXT_MONTHS_INSTABOOK_OWNER, useCalendarFocus, useListingsFilterContext } from '../../hooks';
import useFocusedEvents from '../../hooks/useFocusedEvents';
import CalendarControls from '../CalendarControls';
import CalendarDropdown from '../CalendarDropdown';
import CalendarMonthAgendaView from '../CalendarMonthAgendaView';
import InstabookMonthAgendaView from '../InstabookMonthAgendaView';
import MiniCal from '../MiniCal';
import EventDetails from '../EventDetails';
import Filters from '../Filters';
import UnavailableEditor from '../UnavailableEditor';
import CalendarNav from '../CalendarNav';
import InstabookEditorWrapper from '../InstabookEditorWrapper';
import Icon from '../../../common/components/IconDS22';
import { toDateString, todayDate, getMaxNextEvents } from '../../helpers';

import s from './Calendar.module.scss';

type CalendarWrapperProps = {
  toggleMobileMenu?: (value: boolean) => void;
};

const CalendarWrapper: FC<CalendarWrapperProps> = ({ toggleMobileMenu }) => {
  const user = useSelector(getUser);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedInstabookDate, setSelectedInstabookDate] = useState<Date | null>(null);
  const {
    previous,
    next,
    events: months,
    instabookEnabled,
    setActiveMonth,
    refresh,
    integrations,
  } = useCalendarContext();
  const { clearFilters, setActiveFilters } = useListingsFilterContext();
  const { setCalendarFocus } = useCalendarFocus();
  const today = todayDate();
  const cappedMonths = getMaxNextEvents(months, today);

  const [syncing, setSyncing] = useState(false);
  const isStaff = !!user.get('is_staff');
  const userId = user.get('id');
  const hasCalendarSyncAccess = isCalendarSyncAllowed(userId, isStaff);
  const hasCalendarSyncIntegrations = integrations.length > 0;
  const showSyncButton = hasCalendarSyncAccess && hasCalendarSyncIntegrations;

  const { boats, firstBoatId } = useBoats();
  const controlsModifier = classnames({ withTabs: instabookEnabled });
  const ref = useRef<HTMLDivElement>(null);
  useFocusedEvents<HTMLDivElement>(ref);

  const isInstabook = useRouteMatch(PATHS.INSTABOOK);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
    if (toggleMobileMenu) toggleMobileMenu(!menuOpen);
  };

  const jumpToToday = () => {
    const startOfTodaysMonth = startOfMonth(todayDate());
    setActiveMonth(toDateString(startOfTodaysMonth));
    setCalendarFocus({ selectedDate: toDateString(todayDate()) });
    handleMenuToggle();
  };

  const handleActivityClick = useCallback(() => {
    clearFilters();
  }, [clearFilters]);

  const handleInstabookClick = useCallback(() => {
    const hashId = boats.find((boat) => boat.boat_id === firstBoatId)?.id;
    if (hashId) {
      setActiveFilters([hashId]);
    }
  }, [boats, firstBoatId, setActiveFilters]);

  const showNotification = (heading: string) => {
    toast.dismiss();
    toast(
      <Notification heading={heading} />,
      { autoClose: 5000 },
    );
  };

  const handleSyncAllListings = useCallback(async () => {
    setSyncing(true);
    const response = await apiFetch('/calendar/integrations/bulk_sync/', {
      method: 'GET',
    });
    if (response.ok) {
      /**
       * This delay is a temporary workaround to give the backend enough time to
       * complete syncing, ensuring we fetch the latest events afterward.
       * TODO: Replace this temporary workaround with a proper solution once backend
       * sync reliability improves.
       * [sc-70718] - see this Shortcut story for the solution and more details.
       */
      setTimeout(() => {
        refresh();
        showNotification(`Synchronized ${integrations.length ? 'calendars' : 'calendar'} successfully`);
        setSyncing(false);
      }, 3000);
    } else {
      showNotification(`Failed to synchronize ${integrations.length ? 'calendars' : 'calendar'}`);
      setSyncing(false);
    }
  }, [integrations.length, refresh]);

  return (
    <div
      className={getClassName(
        s,
        'root',
        classnames({
          withPinnedMessage: boats.length === 0,
        }),
      )}
    >
      <CalendarControls classNameModifier={controlsModifier}>
        {instabookEnabled && (
          <CalendarNav
            handleActivityClick={handleActivityClick}
            handleInstabookClick={handleInstabookClick}
          />
        )}
        <Media
          queries={{
            small: MEDIA_QUERIES.NOT_DESKTOP,
            large: MEDIA_QUERIES.LARGE_DESKTOP,
          }}
        >
          {(matches) => (
            <>
              {matches.small && (
                <>
                  <CalendarDropdown classNameModifier={controlsModifier} />
                  <Filters classNameModifier={isInstabook ? 'instabook' : ''} />
                </>
              )}
              {matches.large && <MiniCal selectedInstabookDate={selectedInstabookDate} />}
            </>
          )}
        </Media>
        {boats.length > 0 ? (
          <Media
            queries={{
              small: MEDIA_QUERIES.NOT_DESKTOP,
            }}
          >
            {(matches) => (
              matches.small && (
                <div className={getClassName(s, 'mobileActionMenu', classnames({ isInstabook }))}>
                  <div className={getClassName(s, 'toggleWrapper', classnames({ menuOpen }))}>
                    {showSyncButton && (
                      <Route
                        exact
                        path={PATHS.CALENDAR}
                      >
                        <Button
                          onClick={handleSyncAllListings}
                          type="button"
                          className={s.connectCalendar}
                          classNameModifier="secondary noMargin"
                          submitting={syncing}
                          fullWidth
                        >
                          <Icon id="reload" size="m" />
                          Sync
                        </Button>
                      </Route>
                    )}
                    <Button
                      type="button"
                      onClick={jumpToToday}
                      classNameModifier="secondary noMargin"
                      fullWidth
                    >
                      <Icon
                        id="arrow-forward"
                        size="m"
                      />
                      Today
                    </Button>
                    <Route
                      exact
                      path={PATHS.CALENDAR}
                    >
                      <UnavailableEditor
                        modalName="unavailableEditor"
                        onOpenModal={handleMenuToggle}
                      />
                    </Route>
                    <Route
                      exact
                      path={PATHS.INSTABOOK}
                    >
                      <InstabookEditorWrapper
                        onOpenModal={handleMenuToggle}
                        selectedInstabookDate={selectedInstabookDate}
                      />
                    </Route>
                  </div>
                  <button
                    type="button"
                    className={s.mobileActionMenuToggle}
                    onClick={handleMenuToggle}
                  >
                    <Icon id={menuOpen ? 'x' : 'dots-three'} size="xl" />
                  </button>
                </div>
              )
            )}
          </Media>
        ) : (
          <p className={getClassName(s, 'message', classnames('unpublished', { raised: hasCalendarSyncAccess }))}>
            <Svg
              href="#more-info"
              className={s.icon}
            />
            Some features are not available because your listings are unpublished.
          </p>
        )}
      </CalendarControls>
      <div className={s.wrapper}>
        {!previous.canLoad && (
          <p className={s.message}>
            <Svg
              href="#more-info"
              className={s.icon}
            />
            Your Calendar supports up to 12 months history from today’s date.
          </p>
        )}
        {previous.canLoad && !previous.isLoading && <Waypoint onEnter={previous.loadMore} />}
        {previous.canLoad && <LoadingSpinner>Loading Data</LoadingSpinner>}
        {/* The key is used to prevent unneeded remounting and scrolling side-effects */}
        <ScrollToMe key={`scroll-to-me-${months.length > 0 && months[0].startDate}`} />
        <div
          ref={ref}
          className={s.list}
        >
          <Media
            queries={{
              large: MEDIA_QUERIES.LARGE_DESKTOP,
            }}
          >
            {(matches) => (
              matches.large
              && (
                <Filters
                  classNameModifier={isInstabook ? 'instabook' : ''}
                  syncAllListings={showSyncButton ? handleSyncAllListings : undefined}
                  hasCalendarSyncAccess={hasCalendarSyncAccess}
                  syncing={syncing}
                />
              )
            )}
          </Media>
          <Route
            exact
            path={PATHS.CALENDAR}
          >
            {cappedMonths.map(({ events, startDate }) => (
              <CalendarMonthAgendaView
                key={startDate}
                events={events}
                startDate={startDate}
              />
            ))}
          </Route>
          <Route
            exact
            path={PATHS.INSTABOOK}
          >
            {months.map(({ events, startDate }) => (
              <InstabookMonthAgendaView
                key={startDate}
                events={events}
                startDate={startDate}
                setSelectedInstabookDate={setSelectedInstabookDate}
              />
            ))}
          </Route>
        </div>
        {next.canLoad && next.isLoading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : (
          <Waypoint
            onEnter={next.loadMore}
            bottomOffset="-150"
          />
        )}
        {!next.canLoad && (
          <p className={getClassName(s, 'message', 'future')}>
            <Svg
              href="#more-info"
              className={s.icon}
            />
            {`Your Calendar supports events up to ${
              isInstabook ? MAX_NEXT_MONTHS_INSTABOOK_OWNER : MAX_NEXT_MONTHS
            } months from today’s date.`}
          </p>
        )}
      </div>

      <EventDetails />
    </div>
  );
};

export default CalendarWrapper;
